import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const Products = () => {
  const products = [
    {
      title: "Single Phase Air Cooled Servo Stabilizers",
      desc: "Designed for domestic and light industrial applications with reliable voltage correction and equipment protection.",
      capacity: "1 KVA – 25 KVA",
      inputRange: "170V – 270V",
      outputVoltage: "220V / 230V / 240V",
      link: "/products/single-phase",
      image: "https://images.unsplash.com/photo-1570086625762-7c1396540ac5?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    {
      title: "Three Phase Air Cooled Servo Stabilizers",
      desc: "Commercial and industrial voltage regulators suitable for CNC machines, medical equipment, and production lines.",
      capacity: "6 KVA – 100 KVA",
      inputRange: "170V – 270V / 345V – 465V",
      outputVoltage: "220V / 380V / 400V / 415V",
      link: "/products/three-phase",
      image: "https://images.unsplash.com/photo-1684430598409-750ae3284704?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    {
      title: "Three Phase Oil Cooled Servo Stabilizers",
      desc: "Heavy-duty stabilizers for continuous operation with superior thermal efficiency and high reliability.",
      capacity: "6 KVA – 500 KVA",
      inputRange: "345V – 465V / 295V – 470V",
      outputVoltage: "380V / 400V / 415V",
      link: "/products/oil-cooled",
      image: "https://images.unsplash.com/photo-1671351966453-3f1410780c41?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-main to-primary-dark text-white py-20" data-testid="products-hero-section">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 tracking-tight"
          >
            OUR PRODUCTS
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg font-body max-w-3xl mx-auto opacity-90"
          >
            Comprehensive range of servo voltage stabilizers for every application
          </motion.p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-white" data-testid="products-list-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-12">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 border border-gray-200 rounded-sm overflow-hidden"
                data-testid={`product-item-${index}`}
              >
                <div className="grid lg:grid-cols-2 gap-0">
                  {/* Image */}
                  <div className="h-full">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover min-h-[300px]"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-8 flex flex-col justify-center">
                    <h2 className="text-2xl sm:text-3xl font-heading font-bold text-primary-main mb-4">
                      {product.title}
                    </h2>
                    <p className="text-base text-secondary-main font-body mb-6 leading-relaxed">
                      {product.desc}
                    </p>

                    {/* Specifications */}
                    <div className="space-y-3 mb-6">
                      <div className="flex border-b border-gray-300 pb-2">
                        <span className="font-body font-semibold text-secondary-dark w-40">Capacity:</span>
                        <span className="font-body text-secondary-main">{product.capacity}</span>
                      </div>
                      <div className="flex border-b border-gray-300 pb-2">
                        <span className="font-body font-semibold text-secondary-dark w-40">Input Range:</span>
                        <span className="font-body text-secondary-main">{product.inputRange}</span>
                      </div>
                      <div className="flex border-b border-gray-300 pb-2">
                        <span className="font-body font-semibold text-secondary-dark w-40">Output Voltage:</span>
                        <span className="font-body text-secondary-main">{product.outputVoltage}</span>
                      </div>
                    </div>

                    <Button
                      asChild
                      className="bg-primary-main text-white hover:bg-primary-light rounded-sm w-fit"
                      data-testid={`product-details-button-${index}`}
                    >
                      <Link to={product.link}>
                        View Full Details
                        <ArrowRight className="ml-2" size={18} />
                      </Link>
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
